interface AuraOrbProps {
  variant?: 'purple' | 'cyan' | 'profit' | 'gold' | 'multi';
  size?: number;
  blur?: number;
  opacity?: number;
  className?: string;
  animated?: boolean;
}

export default function AuraOrb({
  variant = 'purple',
  size = 600,
  blur = 80,
  opacity = 0.6,
  className = '',
  animated = true,
}: AuraOrbProps) {
  const colors = {
    purple: {
      primary: '#7c3aed',
      secondary: '#06b6d4',
      tertiary: '#a855f7',
    },
    cyan: {
      primary: '#06b6d4',
      secondary: '#3b82f6',
      tertiary: '#22d3ee',
    },
    profit: {
      primary: '#22c55e',
      secondary: '#10b981',
      tertiary: '#06b6d4',
    },
    gold: {
      primary: '#d4a012',
      secondary: '#fbbf24',
      tertiary: '#f59e0b',
    },
    multi: {
      primary: '#7c3aed',
      secondary: '#06b6d4',
      tertiary: '#22c55e',
    },
  };

  const c = colors[variant];

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {/* Main rotating orb */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          width: size,
          height: size,
          filter: `blur(${blur}px)`,
          opacity,
          animation: animated ? 'aura-rotate 20s linear infinite' : 'none',
        }}
      >
        <div
          className="w-full h-full rounded-full"
          style={{
            background: `conic-gradient(from 0deg, ${c.primary}, ${c.secondary}, ${c.tertiary}, ${c.primary})`,
          }}
        />
      </div>

      {/* Pulsing inner orb */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          width: size * 0.6,
          height: size * 0.6,
          filter: `blur(${blur * 1.2}px)`,
          opacity: opacity * 0.8,
          animation: animated ? 'aura-pulse 4s ease-in-out infinite' : 'none',
        }}
      >
        <div
          className="w-full h-full rounded-full"
          style={{
            background: `radial-gradient(circle, ${c.secondary}80, ${c.primary}40, transparent)`,
          }}
        />
      </div>

      {/* Floating particles */}
      {animated && (
        <>
          <div
            className="absolute rounded-full"
            style={{
              width: 120,
              height: 120,
              top: '30%',
              left: '20%',
              filter: 'blur(40px)',
              opacity: 0.5,
              background: c.primary,
              animation: 'aura-float-1 8s ease-in-out infinite',
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              width: 80,
              height: 80,
              top: '60%',
              right: '25%',
              filter: 'blur(30px)',
              opacity: 0.4,
              background: c.secondary,
              animation: 'aura-float-2 6s ease-in-out infinite',
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              width: 60,
              height: 60,
              top: '20%',
              right: '30%',
              filter: 'blur(25px)',
              opacity: 0.3,
              background: c.tertiary,
              animation: 'aura-float-3 10s ease-in-out infinite',
            }}
          />
        </>
      )}

      {/* Breathing ring effect */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border"
        style={{
          width: size * 0.8,
          height: size * 0.8,
          borderColor: `${c.primary}20`,
          borderWidth: 2,
          animation: animated ? 'aura-breathe 6s ease-in-out infinite' : 'none',
        }}
      />
    </div>
  );
}
